import React, { Suspense, useEffect, useState } from 'react'

import { Milliseconds } from '@/utils/milliseconds'

import { Ordem, OrdemStatus } from '../../entities/ordem'
import { OrdemService } from '../../services/ordem.service'
import OrderColumn from './OrderColumn'

const Logo = React.lazy(
  () => import('@/components/common/layout/components/logo'),
)

function useUpdatedDate(updatedAt: Date | string | number) {
  const [difference, setDifference] = useState(0)

  function calculate() {
    // prettier-ignore
    const since = typeof updatedAt === 'string' || typeof updatedAt === 'number'
        ? new Date(updatedAt)
        : updatedAt;

    const newDifference = Math.floor(
      (new Date().getTime() - since.getTime()) / Milliseconds({ minutes: 1 }),
    )

    setDifference(newDifference)
  }

  useEffect(() => {
    calculate()
    const interval = setInterval(calculate, 10000) // 10 seconds
    return () => clearInterval(interval)
  }, [updatedAt])

  return { difference }
}

function useHook() {
  const {
    data: ordens,
    dataUpdatedAt,
    refetch,
  } = OrdemService.fetchOrdensPainel()

  const ordensBloqueadas = (ordens ?? []).filter(
    (ea) => ea.tipoBloqueio !== null,
  )
  const ordensEmAndamento = (ordens ?? []).filter(
    (ea) => ea.tipoBloqueio === null,
  )

  function filterByStatus(status: OrdemStatus[]) {
    return (ordensEmAndamento ?? []).filter((ea) => status.includes(ea.status))
  }

  function sortOrders(a: Ordem, b: Ordem) {
    if (a.entregaEm && b.entregaEm) {
      return new Date(a.entregaEm).getTime() - new Date(b.entregaEm).getTime()
    }

    if (a.aprovadoEm && b.aprovadoEm) {
      return new Date(a.aprovadoEm).getTime() - new Date(b.aprovadoEm).getTime()
    }

    return a.id - b.id
  }

  // Fetch orders every 5 minutes
  // This is to ensure that the data is always fresh without requiring a manual refresh
  useEffect(() => {
    refetch()
    const interval = setInterval(refetch, 5 * 60 * 1000) // 5 minutes
    return () => clearInterval(interval)
  }, [refetch])

  // prettier-ignore
  const orders = {
    BLOCKED:  ordensBloqueadas              .sort(sortOrders) ?? [],
    OPEN:     filterByStatus(['ORCAMENTO']) .sort(sortOrders) ?? [],
    APPROVED: filterByStatus(['APROVACAO']) .sort(sortOrders) ?? [],
    DOING:    filterByStatus(['EXECUCAO'])  .sort(sortOrders) ?? [],
  }

  return { orders, dataUpdatedAt }
}

export default function Component() {
  const { orders, dataUpdatedAt } = useHook()
  const { difference } = useUpdatedDate(dataUpdatedAt)

  return (
    <main className="flex min-h-screen flex-col">
      {/* HEADER */}
      <div className="flex items-center justify-between bg-zinc-800 px-4 py-2">
        {/* LOGO */}
        <Suspense>
          <Logo />
        </Suspense>

        {/* TITLE */}
        <h2 className="text-xl font-light text-white">Ordens de serviço</h2>

        {/* UPDATED AT LABEL */}
        <div>
          <span className="text-sm text-zinc-200">
            Atualizado{' '}
            {difference > 0 ? 'a ' + difference + ' min(s)' : 'agora'}
          </span>
        </div>
      </div>

      {/* GRID */}
      <div className="grid flex-1 grid-cols-4 items-stretch gap-2">
        <OrderColumn
          label="Em orçamento"
          background="bg-yellow-400"
          ordens={orders.OPEN}
        />
        <OrderColumn
          label="Aprovadas"
          background="bg-blue-300"
          ordens={orders.APPROVED}
        />
        <OrderColumn
          label="Em andamento"
          background="bg-lime-400"
          ordens={orders.DOING}
        />
        <OrderColumn
          label="Bloqueadas"
          background="bg-red-500"
          ordens={orders.BLOCKED}
        />
      </div>
    </main>
  )
}
